import React, { useRef, useState, useEffect, useCallback } from 'react';
import { PenTool, RotateCcw, Download, Check, X, User, Calendar, Clock, ChevronDown } from 'lucide-react';
import driverService from '../../services/driverService';
import { useAuth } from '../../contexts/AuthContext';

const resolveDriverDisplayName = (driver = {}) => {
  if (!driver) return 'Driver';
  if (typeof driver.name === 'string' && driver.name.trim()) {
    return driver.name.trim();
  }

  const nameFromParts = [driver.firstName, driver.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (nameFromParts) {
    return nameFromParts;
  }

  if (typeof driver.email === 'string' && driver.email.trim()) {
    return driver.email.trim();
  }

  return driver.driverId || driver.employeeId || driver.id || 'Driver';
};

const resolveDriverIdentifier = (driver = {}) => {
  return driver.id
    || driver.driverId
    || driver.employeeId
    || driver.userId
    || driver.firebaseUid
    || driver.uid
    || driver.email
    || '';
};

const DigitalSignature = ({ onSignatureCapture, inspectionData, existingSignature = null, disabled = false }) => {
  const canvasRef = useRef(null);
  const { company, userData } = useAuth();
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState(existingSignature);
  const [showSignaturePad, setShowSignaturePad] = useState(!existingSignature);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const isDriver = userData?.role === 'driver';
  const [signatureData, setSignatureData] = useState({
    driverId: '',
    driverName: '',
    employeeId: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].slice(0, 5),
    signatureDataUrl: existingSignature?.signatureDataUrl || null,
    timestamp: existingSignature?.timestamp || null
  });

  const applyDriverSelection = useCallback((driver) => {
    if (!driver) {
      return;
    }

    const driverId = resolveDriverIdentifier(driver);
    if (!driverId) {
      return;
    }

    setSignatureData((prev) => ({
      ...prev,
      driverId,
      driverName: resolveDriverDisplayName(driver),
      employeeId: driver.employeeId || driver.driverId || prev.employeeId || ''
    }));
  }, []);

  const matchDriverToCurrentUser = useCallback((driverList = []) => {
    if (!userData) {
      return null;
    }

    const email = userData.email?.toLowerCase();
    const uid = userData.uid?.toLowerCase();
    const userId = userData.id?.toLowerCase();
    const driverId = userData.driverId?.toLowerCase();

    return driverList.find((driver) => email && driver.email?.toLowerCase() === email)
      || driverList.find((driver) => uid && (driver.firebaseUid?.toLowerCase() === uid || driver.userId?.toLowerCase() === uid))
      || driverList.find((driver) => driverId && (driver.driverId?.toLowerCase() === driverId || driver.employeeId?.toLowerCase() === driverId))
      || driverList.find((driver) => userId && driver.id?.toLowerCase() === userId)
      || null;
  }, [userData]);

  // Fetch drivers when component mounts
  useEffect(() => {
    const fetchDrivers = async () => {
      if (!company?.id) return;
      
      try {
        setLoadingDrivers(true);
        const result = await driverService.listDrivers({}, { companyId: company.id });
        const fetchedDrivers = result.drivers || [];
        setDrivers(fetchedDrivers);

        if (isDriver && fetchedDrivers.length) {
          const matchedDriver = matchDriverToCurrentUser(fetchedDrivers);
          if (matchedDriver) {
            applyDriverSelection(matchedDriver);
          }
        }
      } catch (error) {
        console.error('Failed to fetch drivers:', error);
        setDrivers([]);
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchDrivers();
  }, [company?.id, isDriver, matchDriverToCurrentUser, applyDriverSelection]);

  // Auto-select current driver for driver accounts even if match not found yet
  useEffect(() => {
    if (!isDriver || signatureData.driverId) {
      return;
    }

    if (!userData) {
      return;
    }

    const fallbackDriver = {
      id: userData.driverId || userData.uid || userData.id || userData.email,
      driverId: userData.driverId,
      employeeId: userData.employeeId,
      name: userData.displayName,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email
    };

    applyDriverSelection(fallbackDriver);
  }, [isDriver, userData, signatureData.driverId, applyDriverSelection]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Clear canvas with white background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [showSignaturePad]);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (event) => {
    setIsDrawing(true);
    const coords = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (event) => {
    if (!isDrawing) return;
    
    const coords = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
  };

  // Touch events for mobile
  const handleTouchStart = (event) => {
    event.preventDefault();
    const touch = event.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  const handleTouchMove = (event) => {
    event.preventDefault();
    const touch = event.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  const handleTouchEnd = (event) => {
    event.preventDefault();
    const mouseEvent = new MouseEvent('mouseup', {});
    canvasRef.current.dispatchEvent(mouseEvent);
  };

  const handleDriverSelect = (driverId) => {
    const selectedDriver = drivers.find((driver) => resolveDriverIdentifier(driver) === driverId);
    if (selectedDriver) {
      applyDriverSelection(selectedDriver);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    if (disabled) return;
    
    const canvas = canvasRef.current;
    const signatureDataUrl = canvas.toDataURL('image/png');
    
    if (isCanvasEmpty(canvas)) {
      alert('Please provide a signature before saving.');
      return;
    }

    if (!signatureData.driverId) {
      alert('Please select a driver.');
      return;
    }

    const completeSignature = {
      ...signatureData,
      signatureDataUrl,
      timestamp: new Date().toISOString(),
      inspectionId: inspectionData?.vehicleId + '_' + inspectionData?.inspectionType + '_' + Date.now(),
      vehicleId: inspectionData?.vehicleId,
      inspectionType: inspectionData?.inspectionType
    };

    setSignature(completeSignature);
    setShowSignaturePad(false);
    onSignatureCapture(completeSignature);
  };

  const isCanvasEmpty = (canvas) => {
    const ctx = canvas.getContext('2d');
    const pixelBuffer = new Uint32Array(
      ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );
    
    return !pixelBuffer.some(color => color !== 0xffffffff);
  };

  const downloadSignature = () => {
    if (signature?.signatureDataUrl) {
      const link = document.createElement('a');
      link.download = `signature_${signatureData.driverName}_${signatureData.date}.png`;
      link.href = signature.signatureDataUrl;
      link.click();
    }
  };

  const editSignature = () => {
    setShowSignaturePad(true);
    setSignature(null);
  };

  if (signature && !showSignaturePad) {
    return (
      <div className="space-y-6">
        {/* Signature Display */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Check className="h-6 w-6 text-green-600 mr-2" />
              Inspection Signed
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={downloadSignature}
                className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-800 transition-colors"
                title="Download Signature"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </button>
              <button
                onClick={editSignature}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                title="Edit Signature"
              >
                <PenTool className="h-4 w-4 mr-1" />
                Edit
              </button>
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <img 
              src={signature.signatureDataUrl} 
              alt="Digital Signature"
              className="max-w-full h-auto"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center text-gray-600">
              <User className="h-4 w-4 mr-2" />
              <div>
                <p className="font-medium">Driver Name</p>
                <p>{signature.driverName}</p>
                {signature.employeeId && <p>ID: {signature.employeeId}</p>}
              </div>
            </div>
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2" />
              <div>
                <p className="font-medium">Date</p>
                <p>{new Date(signature.date).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex items-center text-gray-600">
              <Clock className="h-4 w-4 mr-2" />
              <div>
                <p className="font-medium">Time</p>
                <p>{signature.time}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inspection Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inspection Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-500">Vehicle ID</p>
              <p className="text-gray-900">{inspectionData?.vehicleId}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Inspection Type</p>
              <p className="text-gray-900 capitalize">{inspectionData?.type}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Checklist Items</p>
              <p className="text-gray-900">{Object.keys(inspectionData?.checklist || {}).length}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Photos</p>
              <p className="text-gray-900">{inspectionData?.photos?.length || 0}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Defects</p>
              <p className="text-gray-900">{inspectionData?.defects?.length || 0}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Completed</p>
              <p className="text-gray-900">{new Date(signature.timestamp).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
          <PenTool className="h-6 w-6 mr-2" />
          Digital Signature
        </h2>
        <p className="text-gray-600">
          Sign below to complete the {inspectionData?.inspectionType} inspection for vehicle {inspectionData?.vehicleId}
        </p>
      </div>

      {/* Driver Information */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Driver Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isDriver ? 'Driver' : 'Select Driver *'}
            </label>
            {isDriver ? (
              <div className="p-3 border border-blue-200 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-900 font-medium">
                  {signatureData.driverName || resolveDriverDisplayName(userData)}
                </p>
                <p className="text-xs text-blue-800 mt-1">
                  Signed in as a driver. Your profile is automatically linked to this inspection.
                </p>
                {signatureData.employeeId && (
                  <p className="text-xs text-blue-700 mt-1">ID: {signatureData.employeeId}</p>
                )}
              </div>
            ) : loadingDrivers ? (
              <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                Loading drivers...
              </div>
            ) : (
              <div className="relative">
                <select
                  value={signatureData.driverId}
                  onChange={(e) => handleDriverSelect(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white pr-10"
                  required
                >
                  <option value="">Choose a driver...</option>
                  {drivers.map(driver => {
                    const value = resolveDriverIdentifier(driver);
                    return (
                      <option key={value} value={value}>
                        {resolveDriverDisplayName(driver)} {driver.employeeId ? `(${driver.employeeId})` : ''}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            )}
            {signatureData.driverId && !isDriver && (
              <div className="mt-2 p-2 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {signatureData.driverName}
                  {signatureData.employeeId && ` • Employee ID: ${signatureData.employeeId}`}
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={signatureData.date}
              onChange={(e) => setSignatureData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time
            </label>
            <input
              type="time"
              value={signatureData.time}
              onChange={(e) => setSignatureData(prev => ({ ...prev, time: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Signature Pad */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Signature Pad</h3>
          <button
            onClick={clearSignature}
            className="flex items-center px-3 py-1 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Clear
          </button>
        </div>
        
        <div className="border border-gray-300 rounded-lg bg-white">
          <canvas
            ref={canvasRef}
            className="w-full h-48 cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        </div>
        
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Sign above to acknowledge completion of the inspection
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setShowSignaturePad(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={saveSignature}
              disabled={disabled}
              className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                disabled 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <Check className="h-4 w-4 mr-2" />
              Save Signature
            </button>
          </div>
        </div>
      </div>

      {/* Legal Disclaimer */}
      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
        <p className="mb-2">
          <strong>Legal Notice:</strong> By signing this document electronically, you acknowledge that:
        </p>
        <ul className="list-disc list-inside space-y-1 ml-4">
          <li>You have completed the inspection to the best of your ability</li>
          <li>All reported defects are accurate and complete</li>
          <li>You understand the vehicle's operating condition</li>
          <li>This electronic signature has the same legal effect as a handwritten signature</li>
        </ul>
      </div>
    </div>
  );
};

export default DigitalSignature;
